/**
 * @voltxampere/web — multi-page router signal (Sprint 18 Paket B, B1).
 *
 * Mockup zenit-platform.html'de tek sayfa içinde 5 farklı `pane`
 * (AÇILIŞ / SİMÜLATÖR / DEVRELER / INSPECTOR / AYARLAR) geziniyor.
 * B1 sadece state'i tutar — pane'leri görsel olarak swap etmek B2'nin
 * (`<vxa-pane-router>`) işi.
 *
 * URL hash senkronu (`#landing`, `#simulator`, …) ile browser back /
 * forward ve deep-link B2'de eklenecek.
 *
 * Default pane = "simulator". Sprint 11-17 tek-sayfa simulator'unun
 * davranışı korunsun diye boot'ta simulator açılır; landing B3'te
 * gerçekten render olabildiğinde Şefim default'u değiştirir.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";

export const PANE_IDS = ["landing", "simulator", "presets", "inspector", "settings"] as const;
export type PaneId = (typeof PANE_IDS)[number];

/** Roman numeral badge gösterimi mockup'tan birebir. */
export const PANE_NUMERALS: Readonly<Record<PaneId, string>> = {
  landing: "I",
  simulator: "II",
  presets: "III",
  inspector: "IV",
  settings: "V",
};

/** 1-5 keyboard shortcut → PaneId map. */
export const PANE_SHORTCUT_MAP: Readonly<Record<string, PaneId>> = {
  "1": "landing",
  "2": "simulator",
  "3": "presets",
  "4": "inspector",
  "5": "settings",
};

/** Active pane signal. */
export const currentPane = signal<PaneId>("simulator");

export function setPane(pane: PaneId): void {
  if (currentPane.value !== pane) {
    currentPane.value = pane;
  }
}

export function isPaneId(value: string): value is PaneId {
  return (PANE_IDS as readonly string[]).includes(value);
}

/**
 * Parse a `window.location.hash` style string like `"#presets"` into
 * a PaneId. Returns null when the hash is missing or unrecognised so
 * boot fall-through stays on the default pane (`simulator`).
 */
export function parseHashPane(hash: string): PaneId | null {
  if (!hash) return null;
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  return isPaneId(trimmed) ? trimmed : null;
}

/**
 * URL hash ↔ currentPane bidirectional sync (Sprint 18 Paket B B2).
 *
 *   Boot      → location.hash → currentPane (if valid)
 *   setPane   → currentPane → location.hash (replaceState — no
 *               extra history entry per programmatic transition)
 *   hashchange → location.hash → currentPane (browser back / forward)
 *
 * `replaceState` does NOT fire `hashchange`, so the two sides cannot
 * recursively trigger each other — no suppress flag needed. The
 * `target.location.hash === desired` short-circuit covers the only
 * other loop path (subscribe firing on a value that already matches
 * the hash).
 *
 * `replaceState` is intentional over `pushState`: 1-5 shortcuts that
 * fly through panes during exploration shouldn't pile up history
 * entries that the user has to back-button through. Sprint 19+ may
 * revisit per-deliberate-click pushState if Şefim asks for it.
 *
 * Returns a dispose fn so unit tests can attach / detach without
 * leaking listeners between runs.
 */
export function attachRouterHashSync(target: Window = window): () => void {
  // Boot read — let an explicit deep-link override the default
  // `simulator` pane.
  const initial = parseHashPane(target.location.hash);
  if (initial !== null) {
    currentPane.value = initial;
  }

  // currentPane → hash
  const dispose = currentPane.subscribe((pane) => {
    const desired = `#${pane}`;
    if (target.location.hash === desired) return;
    target.history.replaceState(null, "", desired);
  });

  // hash → currentPane (browser back / forward, manual address-bar
  // edits, internal `<a href="#presets">`)
  const onHashChange = (): void => {
    const next = parseHashPane(target.location.hash);
    if (next !== null && next !== currentPane.value) {
      currentPane.value = next;
    }
  };
  target.addEventListener("hashchange", onHashChange);

  return () => {
    dispose();
    target.removeEventListener("hashchange", onHashChange);
  };
}
